/**
 * Bundled demo audio loop. Phase 0 default.
 *
 * The clip is loaded at runtime from `/audio/loop.ogg` (i.e. dropped in
 * `public/audio/loop.ogg`). Sourcing a real CC0 clip is a separate Phase 0
 * deliverable; until then a procedurally-synthesized 110 BPM loop runs in
 * its place so the visualizer still has rhythmic energy for tuning.
 */

const DEMO_URL = '/audio/loop.ogg';

export type DemoSource = {
  /** Connect this to your AnalyserNode pipeline's `input` GainNode. */
  output: AudioNode;
  /** Stop playback and release resources. */
  stop: () => void;
  /** Human label for the HUD. */
  label: string;
};

export async function startDemoClip(ctx: AudioContext): Promise<DemoSource> {
  try {
    const buffer = await loadAudioBuffer(ctx, DEMO_URL);
    return playBufferLoop(ctx, buffer, 'demo clip');
  } catch (err) {
    console.warn('[audio] demo clip not available, using synth fallback:', err);
    return startSynthFallback(ctx);
  }
}

async function loadAudioBuffer(ctx: AudioContext, url: string): Promise<AudioBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${url}: ${res.status} ${res.statusText}`);
  const buf = await res.arrayBuffer();
  return await ctx.decodeAudioData(buf);
}

function playBufferLoop(ctx: AudioContext, buffer: AudioBuffer, label: string): DemoSource {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  const gain = ctx.createGain();
  gain.gain.value = 0.9;
  source.connect(gain);
  gain.connect(ctx.destination);
  source.start();
  return {
    output: gain,
    stop: () => {
      try {
        source.stop();
      } catch {
        // already stopped
      }
      source.disconnect();
      gain.disconnect();
    },
    label,
  };
}

/**
 * Procedural fallback: a 2-bar loop at 110 BPM with kick, hi-hat, bass, and
 * a slow pad. Built from triggered envelope + oscillator nodes so it costs
 * nothing if the OGG is unavailable.
 */
function startSynthFallback(ctx: AudioContext): DemoSource {
  const master = ctx.createGain();
  master.gain.value = 0.7;
  master.connect(ctx.destination);

  const bpm = 110;
  const beatSec = 60 / bpm;
  const barSec = beatSec * 4;
  const startTime = ctx.currentTime + 0.05;

  const stops: Array<() => void> = [];

  // Pad: slow detuned saw chord, AM with a slow LFO.
  const pad = ctx.createOscillator();
  pad.type = 'sawtooth';
  pad.frequency.value = 110; // A2
  const pad2 = ctx.createOscillator();
  pad2.type = 'sawtooth';
  pad2.frequency.value = 110.5;
  const padFilt = ctx.createBiquadFilter();
  padFilt.type = 'lowpass';
  padFilt.frequency.value = 800;
  padFilt.Q.value = 0.7;
  const padGain = ctx.createGain();
  padGain.gain.value = 0.07;
  pad.connect(padFilt);
  pad2.connect(padFilt);
  padFilt.connect(padGain);
  padGain.connect(master);
  pad.start(startTime);
  pad2.start(startTime);
  stops.push(() => {
    pad.stop();
    pad2.stop();
  });

  // Schedule rolling 2-bar pattern for ~10 minutes; should be plenty.
  const totalLoops = 90;
  for (let loop = 0; loop < totalLoops; loop++) {
    const t0 = startTime + loop * barSec * 2;
    // Kick on every beat (4 to the floor).
    for (let b = 0; b < 8; b++) {
      kick(ctx, master, t0 + b * beatSec);
    }
    // Hi-hat on offbeats.
    for (let b = 0; b < 8; b++) {
      hat(ctx, master, t0 + b * beatSec + beatSec * 0.5);
    }
    // Bass note pattern.
    const bassPattern = [55, 55, 73.42, 55, 55, 65.41, 55, 82.41]; // A1 etc.
    for (let b = 0; b < 8; b++) {
      bass(ctx, master, t0 + b * beatSec, bassPattern[b] ?? 55);
    }
  }

  return {
    output: master,
    stop: () => {
      for (const s of stops) {
        try {
          s();
        } catch {
          // ignore
        }
      }
      master.disconnect();
    },
    label: 'demo synth',
  };
}

function kick(ctx: AudioContext, dest: AudioNode, t: number): void {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(140, t);
  osc.frequency.exponentialRampToValueAtTime(45, t + 0.12);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(1.0, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
  osc.connect(g);
  g.connect(dest);
  osc.start(t);
  osc.stop(t + 0.3);
}

function hat(ctx: AudioContext, dest: AudioNode, t: number): void {
  const buf = ctx.createBuffer(1, 0.05 * ctx.sampleRate, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filt = ctx.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = 6000;
  const g = ctx.createGain();
  g.gain.value = 0.18;
  src.connect(filt);
  filt.connect(g);
  g.connect(dest);
  src.start(t);
}

function bass(ctx: AudioContext, dest: AudioNode, t: number, freq: number): void {
  const osc = ctx.createOscillator();
  const filt = ctx.createBiquadFilter();
  const g = ctx.createGain();
  osc.type = 'sawtooth';
  osc.frequency.value = freq;
  filt.type = 'lowpass';
  filt.frequency.setValueAtTime(900, t);
  filt.frequency.exponentialRampToValueAtTime(180, t + 0.4);
  filt.Q.value = 4;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.32, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
  osc.connect(filt);
  filt.connect(g);
  g.connect(dest);
  osc.start(t);
  osc.stop(t + 0.5);
}
